import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OmCard, Pill, statusTone, HealthRing, GOLD, MUTED, NAVY, FIELD } from "@/components/om/OmBits";

export default function OmScan() {
  const { assetId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("reading");
  const [shift, setShift] = useState("Morning");
  const [values, setValues] = useState({});
  const [observations, setObservations] = useState("");
  const [fault, setFault] = useState({ description: "", severity: "High", fault_category: "Mechanical" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/om/scan/${assetId}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [assetId]);

  const submitReading = async () => {
    const readings = (data.parameters || [])
      .filter((p) => values[p.parameter] !== undefined && values[p.parameter] !== "")
      .map((p) => ({ ...p, actual: Number(values[p.parameter]) }));
    if (!readings.length) return toast.error("Enter at least one reading");
    setBusy(true);
    try {
      const { data: log } = await api.post("/om/daily-logs", { asset_id: assetId, shift, readings, observations });
      setDone(`Shift log filed — ${log.severity}`);
      setValues({}); setObservations("");
      toast.success("Reading logged");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const submitFault = async () => {
    if (!fault.description.trim()) return toast.error("Describe the fault");
    setBusy(true);
    try {
      const { data: t } = await api.post("/om/tickets", { asset_id: assetId, ...fault });
      setDone(`Fault ticket ${t.code} raised — resolve within ${t.sla_hours} hrs`);
      setFault({ description: "", severity: "High", fault_category: "Mechanical" });
      toast.success(`Ticket ${t.code} raised`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }

  const a = data.asset;
  const TABS = [["reading", ClipboardCheck, "Log reading"], ["fault", AlertTriangle, "Raise fault"]];

  return (
    <div className="min-h-screen px-4 py-4" style={{ background: NAVY }} data-testid="om-scan-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold tracking-[0.14em] text-[12px]" style={{ color: GOLD }}>PLAN-SARTHI</span>
          <Link to="/om/assets" className="text-[12px] inline-flex items-center gap-1" style={{ color: MUTED }} data-testid="om-scan-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Assets
          </Link>
        </div>

        <OmCard className="mb-3" testid="om-scan-asset">
          <div className="flex items-start gap-3">
            <HealthRing value={a.health} size={48} />
            <div className="min-w-0">
              <div className="text-[11px]" style={{ color: MUTED }}>{a.code}</div>
              <h1 className="text-white font-semibold text-[17px] leading-snug">{a.name}</h1>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <Pill tone={statusTone(a.status)}>{a.status}</Pill>
                <Pill tone="gold">{a.category}</Pill>
                {a.open_tickets ? <Pill tone="bad">{a.open_tickets} open</Pill> : null}
              </div>
              <p className="text-[11.5px] mt-1.5" style={{ color: MUTED }}>
                {[a.site, a.building, a.zone].filter(Boolean).join(" · ") || "Location not set"}
              </p>
            </div>
          </div>
        </OmCard>

        {done ? (
          <OmCard className="mb-3" testid="om-scan-success">
            <div className="flex items-center gap-2 text-[13px]" style={{ color: "#00C896" }}>
              <CheckCircle2 className="w-4 h-4" /> {done}
            </div>
          </OmCard>
        ) : null}

        <div className="flex gap-2 mb-3">
          {TABS.map(([k, Icon, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`om-scan-tab-${k}`}
              className="flex-1 h-10 text-[13px] font-medium inline-flex items-center justify-center gap-1.5 border"
              style={{ borderRadius: 8, borderColor: tab === k ? GOLD : "rgba(201,168,76,0.2)",
                background: tab === k ? GOLD : "transparent", color: tab === k ? NAVY : MUTED }}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {!data.can_write ? (
          <OmCard testid="om-scan-readonly"><p className="text-[12.5px]" style={{ color: MUTED }}>Your role has read-only access to O&M.</p></OmCard>
        ) : tab === "reading" ? (
          <OmCard testid="om-scan-reading-card">
            <div className="mb-3">
              <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Shift</Label>
              <select value={shift} onChange={(e) => setShift(e.target.value)} className="w-full h-10 px-2 text-[13px] border"
                style={{ ...FIELD, borderRadius: 6 }} data-testid="om-scan-shift">
                {["Morning", "Afternoon", "Night"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-2.5">
              {data.parameters.map((p) => (
                <div key={p.parameter}>
                  <Label className="text-[11.5px]" style={{ color: MUTED }}>
                    {p.parameter} <span style={{ color: GOLD }}>({p.unit})</span> · expected {p.expected_min}–{p.expected_max}
                  </Label>
                  <Input type="number" inputMode="decimal" value={values[p.parameter] ?? ""}
                    onChange={(e) => setValues({ ...values, [p.parameter]: e.target.value })}
                    className="h-10" style={FIELD} data-testid={`om-scan-reading-${p.parameter.replace(/\s+/g, "-").toLowerCase()}`} />
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Observations</Label>
              <Textarea rows={2} value={observations} onChange={(e) => setObservations(e.target.value)} style={FIELD} data-testid="om-scan-observations" />
            </div>
            <Button onClick={submitReading} disabled={busy} className="w-full h-11 mt-3 font-semibold"
              style={{ background: GOLD, color: NAVY }} data-testid="om-scan-submit-reading">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} File shift log
            </Button>
          </OmCard>
        ) : (
          <OmCard testid="om-scan-fault-form">
            <div className="grid grid-cols-2 gap-2">
              {[["severity", ["Critical", "High", "Medium", "Low"]], ["fault_category", ["Electrical", "Mechanical", "Instrumentation", "Civil", "Software", "Other"]]].map(([k, opts]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>{k === "severity" ? "Severity" : "Category"}</Label>
                  <select value={fault[k]} onChange={(e) => setFault({ ...fault, [k]: e.target.value })}
                    className="w-full h-10 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid={`om-scan-${k}`}>
                    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>What is wrong?</Label>
              <Textarea rows={4} value={fault.description} onChange={(e) => setFault({ ...fault, description: e.target.value })}
                placeholder="Describe the fault as you see it on site" style={FIELD} data-testid="om-scan-fault-description" />
            </div>
            <Button onClick={submitFault} disabled={busy} className="w-full h-11 mt-3 font-semibold"
              style={{ background: GOLD, color: NAVY }} data-testid="om-scan-submit-fault">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Raise fault ticket
            </Button>
          </OmCard>
        )}

        {data.open_tickets?.length ? (
          <OmCard className="mt-3" testid="om-scan-open-tickets">
            <h2 className="text-white font-semibold text-[13.5px] mb-2">Open tickets on this asset</h2>
            <div className="space-y-2">
              {data.open_tickets.map((t) => (
                <div key={t.code} className="text-[12px] flex items-start gap-2">
                  <Pill tone={statusTone(t.severity)}>{t.severity}</Pill>
                  <span className="text-white flex-1 min-w-0">{t.code} · {t.description}</span>
                </div>
              ))}
            </div>
          </OmCard>
        ) : null}
      </div>
    </div>
  );
}
